import { collection, query, orderBy, limit, getDocs, addDoc, serverTimestamp, where, deleteDoc, doc } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Activity types for tracking different actions
 */
export const ACTIVITY_TYPES = {
  ORDER_CREATED: 'order_created',
  ORDER_UPDATED: 'order_updated',
  ORDER_STATUS_CHANGED: 'order_status_changed',
  ORDER_ARCHIVED: 'order_archived',
  ORDER_UNARCHIVED: 'order_unarchived',
  CLIENT_CREATED: 'client_created',
  CLIENT_UPDATED: 'client_updated',
  CLIENT_BLACKLISTED: 'client_blacklisted',
  CLIENT_BLACKLIST_REMOVED: 'client_blacklist_removed',
  EQUIPMENT_CREATED: 'equipment_created',
  EQUIPMENT_UPDATED: 'equipment_updated',
  TECHNICIAN_CREATED: 'technician_created',
  TECHNICIAN_UPDATED: 'technician_updated',
};

/**
 * Log an activity event and maintain only the last 5 activities
 * @param {string} type - Activity type from ACTIVITY_TYPES
 * @param {string} description - Human readable description
 * @param {Object} metadata - Additional data about the activity
 * @param {string} userId - User who performed the action
 * @returns {Promise<string>} - Document ID of the created activity
 */
export const logActivity = async (type, description, metadata = {}, userId = null) => {
  try {
    const activityData = {
      type,
      description,
      metadata,
      userId,
      timestamp: serverTimestamp(),
      createdAt: new Date() // Fallback for sorting
    };

    // Add the new activity
    const docRef = await addDoc(collection(db, 'activity_log'), activityData);
    
    // Clean up old activities to keep only the last 5
    await cleanupOldActivities();
    
    return docRef.id;
  } catch (error) {
    console.error('Error logging activity:', error);
    throw error;
  }
};

/**
 * Clean up old activities to keep only the last 5
 * @returns {Promise<void>}
 */
const cleanupOldActivities = async () => {
  try {
    // Get all activities ordered by timestamp (newest first)
    const q = query(
      collection(db, 'activity_log'),
      orderBy('timestamp', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const activities = querySnapshot.docs;

    // If we have more than 5 activities, delete the excess ones
    if (activities.length > 5) {
      const activitiesToDelete = activities.slice(5); // Keep first 5, delete the rest
      
      // Delete old activities in batches
      const deletePromises = activitiesToDelete.map(activityDoc => 
        deleteDoc(doc(db, 'activity_log', activityDoc.id))
      );
      
      await Promise.all(deletePromises);
    }
  } catch (error) {
    console.error('Error cleaning up old activities:', error);
    // Don't throw error here to prevent breaking the main activity logging
  }
};

/**
 * Get recent activities for dashboard
 * @param {number} limitCount - Number of activities to retrieve
 * @returns {Promise<Array>} - Array of recent activities
 */
export const getRecentActivities = async (limitCount = 10) => {
  try {
    const q = query(
      collection(db, 'activity_log'),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate() || doc.data().createdAt
    }));
  } catch (error) {
    console.error('Error fetching recent activities:', error);
    
    // If collection doesn't exist yet, return empty array
    if (error.code === 'permission-denied' || error.message.includes('does not exist')) {
      return [];
    }
    
    throw error;
  }
};

/**
 * Get activities for a specific user
 * @param {string} userId - User ID to filter by
 * @param {number} limitCount - Number of activities to retrieve
 * @returns {Promise<Array>} - Array of user activities
 */
export const getUserActivities = async (userId, limitCount = 20) => {
  try {
    const q = query(
      collection(db, 'activity_log'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate() || doc.data().createdAt
    }));
  } catch (error) {
    console.error('Error fetching user activities:', error);
    return [];
  }
};

/**
 * Helper functions to create specific activity descriptions
 */
export const createOrderActivity = (action, orderFolio, additionalInfo = '') => {
  const descriptions = {
    created: `Nueva orden creada: ${orderFolio}`,
    updated: `Orden actualizada: ${orderFolio}`,
    status_changed: `Estado de orden cambiado: ${orderFolio}${additionalInfo ? ` - ${additionalInfo}` : ''}`
  };
  
  return descriptions[action] || `Orden ${action}: ${orderFolio}`;
};

export const createClientActivity = (action, clientName, additionalInfo = '') => {
  const descriptions = {
    created: `Cliente registrado: ${clientName}`,
    updated: `Cliente actualizado: ${clientName}`,
    archived: `Cliente archivado: ${clientName}`,
    unarchived: `Cliente desarchivado: ${clientName}`
  };
  
  return descriptions[action] || `Cliente ${action}: ${clientName}`;
};

export const createEquipmentActivity = (action, equipmentModel, additionalInfo = '') => {
  const descriptions = {
    created: `Equipo catalogado: ${equipmentModel}`,
    updated: `Equipo actualizado: ${equipmentModel}`,
    archived: `Equipo archivado: ${equipmentModel}`,
    unarchived: `Equipo desarchivado: ${equipmentModel}`
  };
  
  return descriptions[action] || `Equipo ${action}: ${equipmentModel}`;
};

export const createTechnicianActivity = (action, technicianName, additionalInfo = '') => {
  const descriptions = {
    created: `Técnico registrado: ${technicianName}`,
    updated: `Técnico actualizado: ${technicianName}`,
    archived: `Técnico archivado: ${technicianName}`,
    unarchived: `Técnico desarchivado: ${technicianName}`
  };
  
  return descriptions[action] || `Técnico ${action}: ${technicianName}`;
};

/**
 * Get relative time description
 * @param {Date} date - Date to compare
 * @returns {string} - Relative time description
 */
export const getRelativeTime = (date) => {
  if (!date) return '';
  
  const now = new Date();
  const diffInMs = now - date;
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  if (diffInMinutes < 1) {
    return 'Hace un momento';
  } else if (diffInMinutes < 60) {
    return `Hace ${diffInMinutes} minuto${diffInMinutes !== 1 ? 's' : ''}`;
  } else if (diffInHours < 24) {
    return `Hace ${diffInHours} hora${diffInHours !== 1 ? 's' : ''}`;
  } else if (diffInDays < 7) {
    return `Hace ${diffInDays} día${diffInDays !== 1 ? 's' : ''}`;
  } else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return `Hace ${weeks} semana${weeks !== 1 ? 's' : ''}`;
  } else if (diffInDays < 365) {
    const months = Math.floor(diffInDays / 30);
    return `Hace ${months} mes${months !== 1 ? 'es' : ''}`;
  } else {
    const years = Math.floor(diffInDays / 365);
    return `Hace ${years} año${years !== 1 ? 's' : ''}`;
  }
};

/**
 * Get icon and color for activity type
 * @param {string} type - Activity type
 * @returns {Object} - Icon component and color class
 */
export const getActivityIcon = (type) => {
  // This will be imported in the component that uses it
  const iconMap = {
    [ACTIVITY_TYPES.ORDER_CREATED]: { icon: 'FileText', color: 'text-blue-600' },
    [ACTIVITY_TYPES.ORDER_UPDATED]: { icon: 'FileText', color: 'text-blue-500' },
    [ACTIVITY_TYPES.ORDER_STATUS_CHANGED]: { icon: 'FileText', color: 'text-blue-700' },
    [ACTIVITY_TYPES.CLIENT_CREATED]: { icon: 'Users', color: 'text-green-600' },
    [ACTIVITY_TYPES.CLIENT_UPDATED]: { icon: 'Users', color: 'text-green-500' },
    [ACTIVITY_TYPES.CLIENT_BLACKLISTED]: { icon: 'AlertTriangle', color: 'text-red-600' },
    [ACTIVITY_TYPES.CLIENT_BLACKLIST_REMOVED]: { icon: 'UserCheck', color: 'text-green-600' },
    [ACTIVITY_TYPES.EQUIPMENT_CREATED]: { icon: 'Package', color: 'text-purple-600' },
    [ACTIVITY_TYPES.EQUIPMENT_UPDATED]: { icon: 'Package', color: 'text-purple-500' },
    [ACTIVITY_TYPES.TECHNICIAN_CREATED]: { icon: 'UserCheck', color: 'text-orange-600' },
    [ACTIVITY_TYPES.TECHNICIAN_UPDATED]: { icon: 'UserCheck', color: 'text-orange-500' },
  };
  
  return iconMap[type] || { icon: 'Activity', color: 'text-gray-600' };
};